import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BRAND } from "@/data/content";

const LINKS = [
  { label: "Experience", href: "#experience", testid: "nav-link-experience" },
  { label: "Manifesto", href: "#manifesto", testid: "nav-link-manifesto" },
  { label: "Favorites", href: "#favorites", testid: "nav-link-favorites" },
  { label: "About", href: "#about", testid: "nav-link-about" },
  { label: "Gallery", href: "#gallery", testid: "nav-link-gallery" },
  { label: "Policy", href: "/policy", testid: "nav-link-policy" },
  { label: "Inquire", href: "#contact", testid: "nav-link-inquire" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
          scrolled
            ? "border-b border-amber-500/10 bg-[#0A0908]/85 shadow-[0_8px_32px_rgba(0,0,0,0.6)] backdrop-blur-xl"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <nav className="flex items-center justify-between px-6 py-4 sm:px-10 lg:px-16">
          <a
            href="#experience"
            data-testid="brand-logo"
            className="flex items-center gap-2.5"
          >
            <img
              src={BRAND.emblem}
              alt="Rock Raven emblem"
              className="h-14 w-auto sm:h-16"
            />
            <span className="font-serif text-2xl uppercase tracking-[0.2em] text-amber-50">
              {BRAND.name}
            </span>
            <span className="hidden font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 lg:inline">
              Live Fire Cooking
            </span>
          </a>

          <div className="hidden items-center gap-8 md:flex">
            {LINKS.slice(0, 6).map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={link.testid}
                className="font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-400 transition-colors duration-300 hover:text-amber-400"
              >
                {link.label}
              </a>
            ))}
            <a
              href="#contact"
              data-testid="nav-link-inquire"
              className="border border-amber-500/40 bg-amber-500/10 px-5 py-2 font-mono text-[11px] uppercase tracking-[0.25em] text-amber-300 transition-all duration-300 hover:border-amber-400 hover:bg-amber-500/20 hover:text-amber-100"
            >
              Book an Event
            </a>
          </div>

          <button
            data-testid="mobile-menu-button"
            aria-label="Open menu"
            onClick={() => setOpen(true)}
            className="text-amber-100 md:hidden"
          >
            <Menu className="size-6" strokeWidth={1.5} />
          </button>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] flex flex-col bg-[#0A0908]"
          >
            <div className="flex items-center justify-between px-6 py-4">
              <span className="font-serif text-lg uppercase tracking-[0.2em] text-amber-50">
                {BRAND.name}
              </span>
              <button
                data-testid="mobile-menu-close"
                aria-label="Close menu"
                onClick={() => setOpen(false)}
                className="text-amber-100"
              >
                <X className="size-6" strokeWidth={1.5} />
              </button>
            </div>
            <div className="flex flex-1 flex-col justify-center gap-2 px-8">
              {LINKS.map((link, i) => (
                <motion.a
                  key={link.href}
                  href={link.href}
                  data-testid={`mobile-${link.testid}`}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.07, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className="border-b border-neutral-800/60 py-5 font-serif text-4xl uppercase text-amber-50 transition-colors duration-300 hover:text-amber-400"
                >
                  {link.label}
                </motion.a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
